import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Col, Container, FormGroup, Input, Nav, NavItem, Row, TabContent, TabPane } from 'reactstrap';
import { NavLink } from 'vl-common/src/components/NavLink';
import { first, last, slice } from 'lodash';
import { FaRegCalendarAlt, FaRegHospital } from 'react-icons/fa';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import classnames from 'classnames';
import { ActionDropdown, TableDataView as TableDataView2 } from 'vl-common/src/components/Table/TableDataView';
import qs from 'vl-common/src/lib/query-string';
import { DropDownModal } from 'vl-common/src/components/TimeLine/DropDownModal';
import UploadDocumentModal from 'vl-common/src/components/UploadDocument/UploadDocumentModal';
import { AlertMessage } from 'vl-common/src/components/AlertMessage/AlertMessage';
import useApi from 'vl-common/src/hooks/useApi';
import usePatientByIdQuery from 'vl-common/src/hooks/usePatientByIdQuery';
import useWidthBreakpoint from 'vl-common/src/hooks/useWidthBreakpoint';
import { useIsClientAdmin } from 'vl-common/src/hooks/useIsClientAdmin';
import { GeneratedPagination } from 'vl-common/src/components/TimeLine/GeneratedPagination';
import Datestamp from 'vl-common/src/components/Datestamp';
import DisplayForRoleCode from 'vl-common/src/components/TimeLine/DisplayForRoleCode';
import { RecordingsContainer } from 'vl-common/src/components/TimeLine/RecordingsContainer';
import { Badge, Button, Checkbox, Skeleton, Select } from 'antd';
import { Typography } from 'ALIASED-antd';
import { TimelineAvatar } from 'vl-common/src/components/TimeLine/TimelineAvatar';
import { PatientNotesPanel } from 'vl-common/src/components/TimeLine/PatientNotesPanel';
import { ChooseProviderModal } from 'vl-common/src/components/TimeLine/ChooseProviderModal';
import { useMutation, useQuery, useQueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ChangeCaseRefModal } from 'vl-common/src/components/ChangeCaseRefModal/ChangeCaseRefModal';
import { DischargeCase } from 'vl-common/src/components/DischargeCase/DischargeCase';
import z from 'zod';
import useUser from 'vl-core/hooks/useUser';
import useIsClinician from '../..//hooks/useIsClinician';
import { ClinicalNoteHistory } from '../ClinicalNoteHistory';
import styled from '@emotion/styled';
import { alertModal, errorModal, successModal } from '../Modals';
import TaskDisplay from './components/TaskDisplay';
import { Case as CaseType } from 'vl-common/src/schemas/shared/case';
import { UnstyledButton } from '../UnstyledButton';
import RequestManagerModal from './components/RequestManagerModal';
import SuppressForRoleCode from './SuppressForRoleCode';
import LabTestOrderTimelineCard from './components/LabTestOrderTimelineCard';
import AppointmentAuditModal from './components/AppointmentAuditModal';
import { PatientInformation } from './components/PatientInformation';
import AppointmentActionMenu from '../Menus/AppointmentActionMenu';
import CaseActionMenu from '../Menus/CaseActionMenu';
import useRouter from 'vl-core/useRouter';
import { isTimelineBackTarget, useTimelineBackURL } from './timelineBackTarget';
import LetterFilePreviewModal from './LetterFilePreviewModal';
import PreviewModal from './PreviewModal';
import { getFileExtension } from '../../utils';
import { getMetaCaseQuery } from 'vl-common/src/schemas/getMetaCase';

const featureCaseLevelNotes = false;

const TopPageLayout = styled.div`
  padding: 1rem 30px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionBox = styled.section`
  width: 100%;
  padding: 1rem 2rem;
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
  background: #fff;
`;

const SplitBoxSections = styled.div`
  display: flex;
  gap: 1rem;
`;

const HalfSection = styled(SectionBox)`
  max-width: 50%;
  flex: 1;
  &:only-child {
    flex: 1 1 min(300px, 50%);
  }
`;

const StyledBoxTitle = styled.h5`
  font-size: 16px !important;
`;

const StyledFormGroup = styled(FormGroup)`
  display: block;
  padding-left: 0;
`;

const HighContrastCheckboxGroup = styled(Checkbox.Group)`
  display: flex;
  flex-direction: column;
  .ant-checkbox-wrapper-disabled {
    & span {
      color: rgba(0, 0, 0, 0.7);
    }
  }
`;

const paramsSchema = z.object({
  case_id: z.string().default(''),

  // ignored and a parameter, but used to denote the selected appointment
  appt_id: z.string().default(''),

  // if set, lists all the cases for that patient
  patient_guid: z.string().default(''), //Patient is the patient_guid for the patient

  // ignored as a parameter, but used to view a team member's case
  team: z.string().optional(),

  // whitelisted entry-point marker — Back button uses this to navigate to a known target
  // instead of relying on history.goBack(); falls back to goBack() when absent or invalid
  from: z.string().optional()
});

type SearchParams = z.infer<typeof paramsSchema>;

const useGetCountOfTimelinesCards = () => {
  const currentBreakPoint = useWidthBreakpoint();
  switch (currentBreakPoint) {
    case 'xxl':
      return 4;
    case 'xl':
      return 3;
    case 'lg':
    case 'md':
    case 'sm':
    case 'xs':
      return 2;
    default:
      return 4;
  }
};

const TableDataView = (props: any) => {
  return <TableDataView2 {...props} />;
};

export async function saveFileFromS3(Api, user_guid, filename) {
  const res = await Api.file.getPreSignedS3Url(user_guid, {
    filename
  });

  const {
    data: { url: tempAWSUrl }
  } = res;

  if (!tempAWSUrl) {
    throw Error(`Could not locate file: "${tempAWSUrl}"`);
  }

  const response = await fetch(tempAWSUrl);
  const blob = await response.blob();

  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

enum TimelineTab {
  Timeline = '1',
  Tasks = '2',
  Documents = '3',
  NonClinicalCaseNotes = '4'
}

const BackButton = styled.button`
  all: unset; /* reset default browser button styles */
  display: inline-flex; /* put icon + text side by side */
  align-items: center; /* vertically center contents */
  cursor: pointer; /* show pointer on hover */

  &:focus {
    outline: 2px solid #007bff; /* customize focus ring */
    outline-offset: 2px;
  }
`;

const CohortDateRange = ({ from, to }) => (
  <span>
    (<Datestamp date={from} format="Date only (localised) - 03/02/2022" />
    {' - '}
    <Datestamp date={to} format="Date only (localised) - 03/02/2022" />)
  </span>
);

// Params arrive plain or base64-encoded, and Buffer.from(s, 'base64') silently decodes plain ones into garbage
const tryDecodeString = (s: string) => {
  if (!s) return s;
  if (/^\d+$/.test(s)) return s; // numbers
  if (s.includes('-')) return s; // hyphens, and so uuids

  try {
    return Buffer.from(s, 'base64').toString();
  } catch (error) {
    console.log('Error while decoding value', error);
    return s;
  }
};

const tryEncodeValue = (val: string | number | undefined | null) => {
  if (val === undefined || val === null || val === '') return '';
  try {
    return Buffer.from(`${val}`).toString('base64');
  } catch (error) {
    console.log('Error while encoding value', error);
    return val;
  }
};

type DocumentType = {
  file_guid: string;
  patient_user_guid: string;
  description?: string;
  uploaded_by?: string;
  created_date?: string;
  letter_type?: string;
  superseded_appt?: boolean;
  document_type?: string;
  amendment?: boolean;
};

export function ReferrerViewTimeLine({
  history,
  search = window.location.href.split('?')[1]
}: {
  history: any;
  search?: string;
}) {
  const { t } = useTranslation('common');
  const { Api, consumed, toasted } = useApi();
  const queryClient = useQueryClient();
  const user = useUser();
  const isClientAdmin = useIsClientAdmin();
  const isClinician = useIsClinician();

  const router = useRouter();

  const [activeTab, setActiveTab] = useState<TimelineTab>(TimelineTab.Timeline);
  const [starter, setStarter] = useState(0);
  const [pageNumberLog, setPageNumberLog] = useState(1);
  const [pageNumberDoc, setPageNumberDoc] = useState(1);

  const [showSelectReasonToViewDocuments, setShowSelectReasonToViewDocuments] = useState(false);
  const [showRequestManagerModal, setShowRequestManagerModal] = useState(false);
  const [requestManagerTitle, setRequestManagerTitle] = useState('');
  const [showChangeCaseRefModal, setShowChangeCaseRefModal] = useState(false);
  const [showDischargeModal, setShowDischargeModal] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [investigation, setInvestigation] = useState<any>(null);
  const [selectedApptId, setSelectedApptId] = useState(null);
  const [showApptAuditModal, setShowApptAuditModal] = useState(false);

  const [selectedReasonToViewDocuments, setSelectedReasonToViewDocuments] = useState('');
  const [enableDocumentsPreview, setEnableDocumentsPreview] = useState(false);
  const [showClinicalNotes, setShowClinicalNotes] = useState(false);
  const [showAlertMessage, setShowAlertMessage] = useState({ visibility: false, msg: '' });

  const [appliedEligibilityPolicies, setAppliedEligibilityPolicies] = useState<string[]>([]);

  const [numPatientNotes, setNumPatientNotes] = useState(0);
  const [caseNoteCount, setCaseNoteCount] = useState(0);

  const [virtualOnlyReason, setVirtualOnlyReason] = useState<string | undefined>(undefined);
  const [eligibilityError, setEligibilityError] = useState<string | null>(null);

  const countOfTimelinesCards = useGetCountOfTimelinesCards();

  const pageState = useMemo<SearchParams>(() => {
    const params = paramsSchema.parse(qs.parse(search, {}));

    return {
      patient_guid: tryDecodeString(params.patient_guid || ''),
      appt_id: tryDecodeString(params.appt_id),
      case_id: tryDecodeString(params.case_id),
      team: String(params?.team === 'true'),
      from: params.from
    };
  }, [search]);

  const buildTimelineBackURL = useTimelineBackURL();
  const explicitBackURL = isTimelineBackTarget(pageState.from) ? buildTimelineBackURL(pageState.from) : null;
  const onBackClick = () => {
    if (explicitBackURL) {
      router.push(explicitBackURL);
      return;
    }
    history?.goBack();
  };

  const { data, isLoading: isLoadingPatientDetails } = usePatientByIdQuery(pageState.patient_guid);
  const patientData = data ?? {};

  const {
    data: casesData = [],
    refetch: refetchCases,
    isSuccess: casesLoaded
  } = useQuery({
    queryKey: ['cases', pageState.patient_guid, pageState.team],
    queryFn: async () => {
      if (!pageState.patient_guid) return [];
      const response = await Api.referrers.searchForCases({
        rows: 999,
        patient: pageState.patient_guid,
        first_incomplete: true,
        team: pageState.team,
        all_specs: true
      });
      if (response?.status === 204) {
        consumed(response);
        alertModal({ title: 'No cases found', content: 'No cases found for this patient.' });
      }
      return response?.data?.cases || [];
    },
    enabled: !!pageState.patient_guid,
    refetchOnWindowFocus: false
  });

  const { data: timelineItems = [], refetch: refetchTimelineItems } = useQuery({
    queryKey: ['timeline', pageState?.case_id],
    queryFn: async () => {
      const res = await Api.user.timeline({ case_id: pageState.case_id });
      return res.data.timeline;
    },
    enabled: !!pageState.case_id
  });

  const { data: reasonsToReviewDocuments = [] } = useQuery({
    queryKey: ['reasons'],
    queryFn: async () => {
      const {
        data: { meta }
      } = await Api.meta.GetReasons();
      return meta[0].process_reasons[0].process_reasons;
    }
  });

  const { data: eligibilityCheckbox = [] } = useQuery({
    queryKey: ['eligibilityPolicies', pageState.case_id],
    queryFn: async () => {
      const res = await Api.meta.getEligibilityPolicies(pageState.case_id);
      const eligibilityPolicies = res.data.meta[0].eligibility_policies;
      return (
        eligibilityPolicies?.map((policy) => ({
          label: policy.policy_name,
          value: policy.policy_code
        })) || []
      );
    },
    enabled: !!pageState.case_id
  });

  const { data: caseEligibilityData, refetch: refetchCaseEligibilities } = useQuery({
    queryKey: ['caseEligibility', pageState.case_id, eligibilityCheckbox],
    queryFn: async () => {
      const res = await Api.cases.getCaseEligibility(pageState.case_id);
      return res.data?.case_eligibility?.[0] ?? null;
    },
    enabled: !!pageState.case_id && eligibilityCheckbox.length > 0
  });

  const previousEligibilityPolicies = useMemo(() => {
    if (!caseEligibilityData?.policies) return [];
    return caseEligibilityData.policies
      .filter(({ applied }) => applied === 'true' || applied === true)
      .map(({ policy_code }) => policy_code);
  }, [caseEligibilityData]);

  useEffect(() => {
    if (caseEligibilityData) {
      const applied =
        caseEligibilityData.policies
          ?.filter(({ applied }) => applied === 'true' || applied === true)
          .map(({ policy_code }) => policy_code) ?? [];

      setAppliedEligibilityPolicies(applied);

      if (caseEligibilityData.virtual_only_reason) {
        setVirtualOnlyReason(caseEligibilityData.virtual_only_reason);
      }
    }
  }, [caseEligibilityData]);

  const { data: auditLog = [], refetch: refetchAuditLog } = useQuery({
    queryKey: ['auditLog', pageState.appt_id, pageState.case_id, pageNumberLog],
    queryFn: async () => {
      const res = await Api.admin.AdminUsers.GetAuditLogs(pageState.appt_id, pageState.case_id, pageNumberLog);
      return res.data.gdpr_log || [];
    },
    enabled: !!pageState.case_id
  });

  const { data: documentData = [], refetch: refetchDocuments } = useQuery({
    queryKey: ['documents', pageState.appt_id, pageState.case_id, pageNumberDoc],
    queryFn: async () => {
      const {
        data: { appt_letter }
      } = await Api.admin.AdminUsers.GetDocuments(pageState.appt_id, pageState.case_id, pageNumberDoc);
      return appt_letter?.map((letter) => ({ ...letter, key: letter.file_guid })) || [];
    },
    enabled: !!(pageState.appt_id || pageState.case_id)
  });

  const reopenCaseMutation = useMutation({
    mutationFn: (case_id: string) => Api.cases.postReopenCase({ case_id }),
    onSuccess: async () => await refreshCasesData(),
    onError: (error: any) => {
      const { response } = error;
      if (response?.data?.message) {
        errorModal({ title: 'An error occurred', content: response.data.message });
      } else {
        toasted(error);
      }
    }
  });

  const caseDropdownItems = useMemo(() => {
    const options = [...casesData]
      .sort(
        (lhs: CaseType, rhs: CaseType) =>
          new Date(rhs.case_created_date).getTime() - new Date(lhs.case_created_date).getTime()
      )
      .map((item) => ({ key: item.case_id, value: item.cases_dropdown }));
    return [{ key: 1, value: 'Select an Item' }, ...options];
  }, [casesData]);

  const selectedCase = useMemo((): CaseType | null => {
    if (!casesData.length) return null;

    const caseFromSearchState = casesData.find((c) => c.case_id.toString() === pageState.case_id);
    if (caseFromSearchState) return caseFromSearchState;

    const sortedCases = [...casesData].sort(
      (lhs: CaseType, rhs: CaseType) =>
        new Date(rhs.case_created_date).getTime() - new Date(lhs.case_created_date).getTime()
    );
    return sortedCases[0] || null;
  }, [casesData, pageState.case_id]);

  const updatePageCaseState = useCallback(
    (caseId: string, freshCases?: CaseType[]) => {
      const currentQuery = qs.parse(search, {});
      const targetCase = (freshCases ?? casesData).find((c) => String(c.case_id) === String(caseId));

      if (!targetCase) return;

      const updatedUrlSearch = {
        ...currentQuery,
        case_id: tryEncodeValue(targetCase.case_id),
        appt_id: tryEncodeValue(targetCase.appointment_id)
      };

      const filteredSearchParams = Object.fromEntries(
        Object.entries(updatedUrlSearch).filter(([_, v]) => v !== undefined && v !== null && v !== '')
      );

      router.replace({ search: `?${qs.stringify(filteredSearchParams)}` });
    },
    [casesData, pageState]
  );

  // If timeline page opened with only patient_guid param first available case is selected for the patient
  // this effect syncs this change with pageState
  useEffect(() => {
    if (selectedCase?.case_id && !pageState.case_id) {
      updatePageCaseState(String(selectedCase.case_id));
    }
  }, [selectedCase?.case_id, pageState.case_id]);

  const postEligibilityChanges = async (selectedPolicies: string[]) => {
    const isVirtualOnlySelected = selectedPolicies.includes('VIRTUAL_ONLY');

    if (isVirtualOnlySelected && !virtualOnlyReason) {
      setEligibilityError(t('Virtual Only Reason is required'));
      return;
    }

    setEligibilityError(null);

    const postCaseEligibilityPolicy = async (policyCode: string, applied: boolean) => {
      const reason = policyCode === 'VIRTUAL_ONLY' && applied ? virtualOnlyReason : undefined;
      return Api.referrers.postEligibilityPolicy(pageState.case_id, policyCode, applied, reason);
    };

    const newPolicies = selectedPolicies.filter((n) => !previousEligibilityPolicies.includes(n));
    const droppedPolicies = previousEligibilityPolicies.filter(
      (d: string) => !['CASE_ELIGIBILITY', ...selectedPolicies].includes(d)
    );

    const isVirtualOnlyReasonUpdated =
      isVirtualOnlySelected &&
      previousEligibilityPolicies.includes('VIRTUAL_ONLY') &&
      !newPolicies.includes('VIRTUAL_ONLY');

    const policiesToApply = isVirtualOnlyReasonUpdated ? [...newPolicies, 'VIRTUAL_ONLY'] : newPolicies;

    await Promise.all([
      ...policiesToApply.map((n) => postCaseEligibilityPolicy(n, true)),
      ...droppedPolicies.map((d: string) => postCaseEligibilityPolicy(d, false))
    ]);

    await refetchCaseEligibilities();

    successModal({
      content: 'Required checks applied'
    });
  };

  const onCaseChanged = useCallback(
    (caseId: string) => {
      setShowAlertMessage({ visibility: false, msg: '' });
      const selected = casesData.find((c) => c.case_id === parseInt(caseId));
      if (selected) {
        updatePageCaseState(String(selected.case_id));
      }
      setActiveTab(TimelineTab.Timeline);
      setPageNumberDoc(1);
      setPageNumberLog(1);
    },
    [casesData, updatePageCaseState]
  );

  const toggleTabs = (tab: TimelineTab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const alertTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const displayAlertMessage = (msg = '') => {
    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
    }

    setShowAlertMessage({ visibility: true, msg });

    alertTimeoutRef.current = setTimeout(() => {
      setShowAlertMessage({ visibility: false, msg: '' });
    }, 7000);
  };

  useEffect(() => {
    return () => {
      if (alertTimeoutRef.current) {
        clearTimeout(alertTimeoutRef.current);
      }
    };
  }, []);

  const pageUpdatedLog = (pageNumberParam) => {
    setPageNumberLog(pageNumberParam);
  };
  const pageUpdatedDoc = (pageNumberParam) => {
    setPageNumberDoc(pageNumberParam);
  };

  const getDocumentDisplayTitle = useCallback((doc: DocumentType | null) => {
    if (!doc) return '';

    if (doc?.document_type !== 'Letter') return doc?.description;

    if (doc.superseded_appt) return `${doc.description} - Superseded`;

    if (doc.amendment) return `${doc.description} - Current Version`;

    return doc.description;
  }, []);

  type FileType = {
    patientGuid: string;
    fileName: string;
    isFactsheet?: boolean;
    isImage?: boolean;
    uploadedBy?: string;
    createdDate?: string;
    supersededAppt?: boolean;
    amendment?: boolean;
    title?: string;
  };

  const [isOpenPreviewModal, setIsOpenPreviewModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileType>({ patientGuid: '', fileName: '' });

  const handlePreviewDocument = useCallback(
    async (val: DocumentType) => {
      const fileExtension = getFileExtension(val.file_guid);
      const isDocOrText = ['doc', 'docx', 'txt'].includes(fileExtension.toLowerCase());

      // Fall back to download for Word documents and text files since they cannot be previewed modal
      if (isDocOrText) {
        if (val.letter_type === 'FACTSHEET') {
          const link = document.createElement('a');
          link.href = val.file_guid;
          link.target = '_blank';
          link.download = `${val.description || 'document'}.${fileExtension}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          await saveFileFromS3(Api, val.patient_user_guid, val.file_guid);
        }
        return;
      }

      setIsOpenPreviewModal(true);
      setSelectedFile({
        patientGuid: val.patient_user_guid,
        fileName: val.file_guid,
        isFactsheet: val.letter_type === 'FACTSHEET',
        isImage: val.document_type?.toUpperCase() === 'IMAGE',
        uploadedBy: val.uploaded_by,
        createdDate: val.created_date ? String(val.created_date) : undefined,
        supersededAppt: val.superseded_appt,
        amendment: val.amendment,
        title: getDocumentDisplayTitle(val)
      });
    },
    [Api, getDocumentDisplayTitle]
  );

  const documentsHeaderItems = useMemo(
    () => [
      { display: 'Uploaded By', dataSourceKey: 'uploaded_by' },
      {
        display: 'Created Date',
        dataSourceKey: 'created_date',
        columnDataFormat: ({ data: date }) => <Datestamp date={date} format="Date @ Time - 03/01/2022 @ 08:57" />
      },
      { display: 'Document Type', dataSourceKey: 'document_type' },
      {
        display: 'Document Title',
        dataSourceKey: 'description',
        columnDataFormat: ({ entireData }: { entireData: DocumentType }) => {
          return getDocumentDisplayTitle(entireData);
        }
      },
      {
        display: 'Actions',
        isAction: true,
        actionItems: [
          {
            label: 'Preview',
            onClick: (data: DocumentType) => {
              handlePreviewDocument(data);
            }
          },
          {
            label: 'Download',
            hidden: isClinician,
            onClick: (data: DocumentType) => {
              saveFileFromS3(Api, data.patient_user_guid, data.file_guid);
            }
          }
        ]
      }
    ],
    [getDocumentDisplayTitle, handlePreviewDocument, isClinician]
  );

  const displayRequestManager = () => {
    if (!timelineItems) return;
    const caseDetails = timelineItems.find((a) => a.item_level === 'case');
    const { item_id, item_condition } = caseDetails;
    setShowRequestManagerModal(true);
    setRequestManagerTitle(`${item_id} - ${item_condition}`);
  };

  const closeRequestManagerModal = () => {
    setShowRequestManagerModal(false);
  };

  const refreshCasesData = async () => {
    return await Promise.all([refetchTimelineItems(), refetchCases()]);
  };

  const handleNewCaseCreated = async (caseId: number) => {
    const [, { data: freshCases }] = await refreshCasesData();
    updatePageCaseState(String(caseId), freshCases);
  };

  const defaultDropdownValue = useMemo(() => {
    if (selectedCase) return selectedCase.case_id;
    return caseDropdownItems[1]?.key;
  }, [selectedCase, caseDropdownItems]);

  const handleChangeInvestigation = (inv: unknown) => setInvestigation(inv);
  const handleProviderModalClose = () => setInvestigation(null);

  const handleProviderModalError = (errorMessage: string) =>
    errorModal({ title: 'An error occured', content: errorMessage });

  const handleProviderModalDone = async () => {
    if (investigation?.case_id) {
      queryClient.invalidateQueries({ queryKey: ['investigations', investigation?.case_id] }).then();
      queryClient.invalidateQueries({ queryKey: ['investigations audit', investigation?.case_id] }).then();
    }
    successModal({ title: 'Provider updated' });
    setInvestigation(null);
    await refreshCasesData();
  };

  const handleApptAuditModalClose = () => {
    setSelectedApptId(null);
    setShowApptAuditModal(false);
  };

  const handleChangeCaseRefModalClose = () => setShowChangeCaseRefModal(false);
  const handleCaseRefChanged = async () => {
    handleChangeCaseRefModalClose();
    await refreshCasesData();
  };

  const handleDischargeCaseClose = () => setShowDischargeModal(false);
  const handleDischargeCaseConfirm = (reasonObject: { key: string; value: string }) => {
    const { appt_id, case_id } = pageState;
    const { key } = reasonObject;
    Api.cases
      .postDischargeCase({ appt_id, case_id, discharge_code: key })
      .then(() => {
        refreshCasesData().then();
        successModal({ title: `Discharged Case: ${case_id}` });
      })
      .catch((error) => {
        consumed(error);
        type StatusCode = 417 | 425;
        const backendMessage = error?.response?.data?.message;
        const customMessages: Record<StatusCode, string> = {
          417: 'Unable to discharge case with outstanding appointments',
          425: 'Unable to discharge case with outstanding requests'
        };
        const status_code = error?.response?.status.toString() as StatusCode;
        const message = customMessages[status_code] || backendMessage || 'An error occurred';
        errorModal({ title: `Failed to Discharge Case: ${case_id}`, content: message });
      })
      .finally(() => {
        setShowDischargeModal(false);
      });
  };

  const showEligibilityChecks = !isClinician && !!eligibilityCheckbox.length;
  const handleEligibilityCheckboxChange = (checkedValues) => {
    setEligibilityError(null);
    setAppliedEligibilityPolicies(checkedValues);
  };
  const handleApplyEligibilityChanges = () => postEligibilityChanges(appliedEligibilityPolicies);

  const resetTabSideEffects = () => {
    setSelectedReasonToViewDocuments('');
    setShowClinicalNotes(false);
  };

  const handleTimelineTabClick = () => {
    toggleTabs(TimelineTab.Timeline);
    resetTabSideEffects();
  };

  const handleTasksTabClick = () => {
    toggleTabs(TimelineTab.Tasks);
    resetTabSideEffects();
  };

  const handleNonClinicalNotesTabClick = () => {
    toggleTabs(TimelineTab.NonClinicalCaseNotes);
    resetTabSideEffects();
  };

  const handleDocumentsTabClick = () => {
    if (casesLoaded && !pageState.case_id) {
      displayAlertMessage('Please select the case');
      return;
    }

    if (isClinician && user?.user_guid === selectedCase?.clinician_user_guid) {
      onOpenDocumentsTab();
      return;
    }

    setShowSelectReasonToViewDocuments(true);
  };

  const onOpenDocumentsTab = () => {
    refetchAuditLog().then();
    refetchDocuments().then();
    setEnableDocumentsPreview(true);
    setShowClinicalNotes(true);
    toggleTabs(TimelineTab.Documents);
  };

  const handleReasonModalClose = () => {
    setShowSelectReasonToViewDocuments(false);
    toggleTabs(TimelineTab.Timeline);
    setSelectedReasonToViewDocuments('');
    setShowClinicalNotes(false);
  };

  const handleReasonModalSubmit = async () => {
    if (!selectedReasonToViewDocuments) {
      displayAlertMessage('Please select the reason');
      setEnableDocumentsPreview(false);
      return;
    }

    try {
      await Api.admin.AdminUsers.postReason(pageState.appt_id, pageState.case_id, selectedReasonToViewDocuments);
      setShowSelectReasonToViewDocuments(false);
      onOpenDocumentsTab();
    } catch (error) {
      console.error(error);
      setShowSelectReasonToViewDocuments(true);
    }
  };

  const caseDocmanConsent = !selectedCase?.docman_active ? null : selectedCase.docman_consent;

  const canShowTasksTable =
    !isClinician && !!selectedCase?.client_case_id && pageState.case_id && pageState.patient_guid;

  const appointmentTimelineItems = timelineItems?.filter((item) => item.item_level === 'appointment') ?? [];
  const caseHasNonCancelledAppointment =
    appointmentTimelineItems.filter((item) => item.item_status_code !== 'CANCELLED').length > 0;

  const shower = slice(timelineItems, starter, starter + countOfTimelinesCards);
  const handleTimelinePrev = () => {
    if (starter === 0) return;
    setStarter(starter - 1);
  };
  const handleTimelineNext = () => {
    if (starter + countOfTimelinesCards >= timelineItems.length) return;
    setStarter(starter + 1);
  };

  const shouldShowCaseStatus = !isClinician && selectedCase;

  const { data: caseMeta } = useQuery(
    getMetaCaseQuery({
      pat_guid: selectedCase?.patient_user_guid || undefined
    })
  );

  const showVirtualOnlyReason = appliedEligibilityPolicies.includes('VIRTUAL_ONLY');

  const virtualOnlyReasonOptions = caseMeta?.virtual_only_reason_codes.map((reason) => ({
    value: reason.virtual_only_reason_code,
    label: reason.virtual_only_reason_desc
  }));

  return (
    <div className="referrer-timeline-appointment">
      {pageState.case_id && (
        <QueryClientProvider client={queryClient}>
          <RequestManagerModal
            case_id={pageState.case_id}
            onChangeInvestigation={handleChangeInvestigation}
            show={showRequestManagerModal}
            onClose={closeRequestManagerModal}
            title={requestManagerTitle}
          />
        </QueryClientProvider>
      )}
      {!!investigation && (
        <ChooseProviderModal
          visible={!!investigation}
          patient={patientData}
          investigation={investigation}
          onDone={handleProviderModalDone}
          onErrorMessage={handleProviderModalError}
          onClose={handleProviderModalClose}
        />
      )}

      <AppointmentAuditModal
        show={showApptAuditModal}
        selectedApptId={selectedApptId}
        onClose={handleApptAuditModalClose}
      />

      {/* Modal for Factsheets and Images */}
      {isOpenPreviewModal && (selectedFile.isFactsheet || selectedFile.isImage) && (
        <PreviewModal
          fileName={selectedFile.fileName}
          patientGuid={selectedFile.patientGuid}
          isFactsheet={selectedFile.isFactsheet}
          onClose={() => {
            setIsOpenPreviewModal(false);
            setSelectedFile({ patientGuid: '', fileName: '' });
          }}
        />
      )}

      {isOpenPreviewModal && !selectedFile.isFactsheet && !selectedFile.isImage && (
        <LetterFilePreviewModal
          open
          fileGuid={selectedFile.fileName}
          patientGuid={selectedFile.patientGuid}
          title={selectedFile.title}
          uploadedBy={selectedFile.uploadedBy}
          createdDate={selectedFile.createdDate}
          annotateCurrentLetter={selectedFile.amendment}
          onClose={() => {
            setIsOpenPreviewModal(false);
            setSelectedFile({ patientGuid: '', fileName: '' });
          }}
        />
      )}

      <ChangeCaseRefModal
        isOpen={showChangeCaseRefModal}
        currentRef={selectedCase?.client_case_id}
        case_id={pageState.case_id}
        noBtnHandler={handleChangeCaseRefModalClose}
        confirmHandler={handleCaseRefChanged}
      />

      <DischargeCase
        caseId={pageState.case_id}
        isOpen={showDischargeModal}
        toggle={handleDischargeCaseClose}
        noBtnHandler={handleDischargeCaseClose}
        yesBtnHandler={handleDischargeCaseConfirm}
        yesBtnText="Confirm"
        noBtnText="Cancel"
        subText="Please select a reason why you are discharging the case"
      />

      <UploadDocumentModal
        visible={uploadModalVisible}
        setVisible={setUploadModalVisible}
        appId={pageState.appt_id}
        caseId={pageState.case_id}
        patientId={pageState.patient_guid}
        afterDocumentUpload={refetchDocuments}
      />

      <DropDownModal
        data-testid="reason-modal"
        key={Number(showSelectReasonToViewDocuments)}
        open={showSelectReasonToViewDocuments}
        title="Reason"
        subText="Select a reason to view the documents"
        yesBtnText="Submit"
        noBtnText="Cancel"
        showalertMessage={showAlertMessage}
        dropdownValueChange={setSelectedReasonToViewDocuments}
        reasonList={reasonsToReviewDocuments}
        closeBtnHandler={null}
        noBtnHandler={handleReasonModalClose}
        yesBtnHandler={handleReasonModalSubmit}
        width={750}
      />

      <div style={{ alignItems: 'flex-start', padding: '1rem 1.5rem 0 1.5rem' }}>
        <BackButton className="vl-back-button-holder" onClick={onBackClick} data-testid="go-back-from-timeline">
          <IoIosArrowBack className="icon-back" />
          <span className="font-weight-normal">{t('Back')}</span>
        </BackButton>
      </div>

      <div
        style={{
          margin: '0 1rem',
          borderRadius: '8px',
          marginBottom: '30px'
        }}
      >
        <TopPageLayout>
          <SectionBox>
            <Skeleton loading={isLoadingPatientDetails} />
            {!isLoadingPatientDetails && (
              <PatientInformation
                patient={patientData}
                selectedCase={selectedCase}
                docman_consent={caseDocmanConsent}
                onNewCaseCreated={handleNewCaseCreated}
              />
            )}
          </SectionBox>

          <SplitBoxSections>
            <HalfSection>
              <Skeleton loading={!casesLoaded} />
              {casesLoaded && (
                <div className="referrer-timeline-appointment">
                  <div>{!casesData[0] && <span className="font-weight-bold">{t('No cases found')}</span>}</div>

                  <StyledBoxTitle>{t('Case Selection')}</StyledBoxTitle>
                  <FormGroup className="d-flex flex-row gap-1 align-items-center px-0">
                    <Input
                      className="minimal"
                      type="select"
                      onChange={({ target }) => onCaseChanged(target.value)}
                      value={defaultDropdownValue}
                    >
                      {caseDropdownItems.map((item) => {
                        return (
                          <option key={item.key} value={item.key}>
                            {item.value}
                          </option>
                        );
                      })}
                    </Input>
                    {!isClinician && pageState.case_id && (
                      <CaseActionMenu caseId={pageState.case_id} refetchFn={refreshCasesData} />
                    )}
                  </FormGroup>
                  <AlertMessage message={showAlertMessage.msg} visibility={showAlertMessage.visibility} />
                  {shouldShowCaseStatus && (
                    <span data-testid="selected-case-status">
                      <strong>{t('Status:')}</strong> {selectedCase.status}
                    </span>
                  )}
                </div>
              )}
            </HalfSection>

            {showEligibilityChecks && (
              <HalfSection>
                <StyledBoxTitle>{t('Required Checks')}</StyledBoxTitle>
                <StyledFormGroup switch>
                  <HighContrastCheckboxGroup
                    disabled={isClientAdmin}
                    options={eligibilityCheckbox}
                    defaultValue={previousEligibilityPolicies}
                    onChange={handleEligibilityCheckboxChange}
                    key={pageState.case_id && previousEligibilityPolicies}
                  />
                  {showVirtualOnlyReason && (
                    <div style={{ marginTop: '12px', paddingLeft: '1rem' }}>
                      <div style={{ marginBottom: '4px', fontSize: '14px' }}>
                        {t('Virtual Only Reason')} <span className="vl-product-satsuma">*</span>
                      </div>
                      <Select
                        options={virtualOnlyReasonOptions}
                        placeholder={t('Select Virtual Only Reason')}
                        value={virtualOnlyReason}
                        onChange={(val) => {
                          setEligibilityError(null);
                          setVirtualOnlyReason(val);
                        }}
                        disabled={isClientAdmin}
                        style={{ width: '100%', maxWidth: '300px' }}
                        data-testid="virtual-only-reason"
                        status={eligibilityError ? 'error' : undefined}
                      />
                      {eligibilityError && (
                        <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>{eligibilityError}</div>
                      )}
                    </div>
                  )}
                  {!isClientAdmin && (
                    <Button
                      className={'mt-3'}
                      onClick={handleApplyEligibilityChanges}
                      data-testid="apply-eligibility-changes"
                    >
                      {t('Apply')}
                    </Button>
                  )}
                </StyledFormGroup>
              </HalfSection>
            )}
          </SplitBoxSections>
        </TopPageLayout>

        <div style={{ border: '0px solid lightgray', margin: 0, padding: '0px 30px', width: '100%' }}>
          <div className="content" style={{ padding: '0' }}>
            <Nav tabs className="mt-3 vl-setting-tabs">
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === TimelineTab.Timeline })}
                  data-testid="timeline-tab"
                  onClick={handleTimelineTabClick}
                >
                  {t('Timeline')}
                </NavLink>
              </NavItem>
              <DisplayForRoleCode hbsadmin audit super opshbs>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === TimelineTab.Tasks })}
                    data-testid="tasks-tab"
                    onClick={handleTasksTabClick}
                  >
                    {t('Tasks')}
                  </NavLink>
                </NavItem>
              </DisplayForRoleCode>
              <DisplayForRoleCode hbsadmin audit clinician clinaudit super opshbs>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === TimelineTab.Documents })}
                    data-testid="documents-tab"
                    onClick={handleDocumentsTabClick}
                  >
                    {t('Documents')}
                  </NavLink>
                </NavItem>
              </DisplayForRoleCode>

              {featureCaseLevelNotes && (
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === TimelineTab.NonClinicalCaseNotes })}
                    onClick={handleNonClinicalNotesTabClick}
                  >
                    <div>
                      <Badge count={caseNoteCount} offset={[5, -5]}>
                        {t('Non-Clinical Case Notes')}
                      </Badge>
                    </div>
                  </NavLink>
                </NavItem>
              )}
            </Nav>
            <TabContent className="shadow-sm" activeTab={activeTab}>
              <TabPane tabId="1">
                <Container fluid={true}>
                  <Row>
                    <Col style={{ paddingLeft: '0px', paddingRight: '0px' }}>
                      <div className="linebox vl-flex-col-center">
                        <div className="navigationBox">
                          <UnstyledButton
                            onClick={handleTimelinePrev}
                            data-testid="timeline-previous-button"
                            aria-label={t('Previous')}
                          >
                            <IoIosArrowBack
                              className={classnames('icon-nav', {
                                'icon-nav-deActive':
                                  shower.length < countOfTimelinesCards || last(timelineItems) === last(shower)
                              })}
                              aria-hidden
                            />
                          </UnstyledButton>
                          <div className="orangLine" />
                          <UnstyledButton
                            onClick={handleTimelineNext}
                            data-testid="timeline-next-button"
                            aria-label={t('Next')}
                          >
                            <IoIosArrowForward
                              className={classnames('icon-nav', {
                                'icon-nav-deActive':
                                  shower.length < countOfTimelinesCards || first(timelineItems) === first(shower)
                              })}
                              aria-hidden
                            />
                          </UnstyledButton>
                        </div>

                        <div className="boxContainer">
                          {shower.map((item, index) => {
                            const isAppointment = item.item_level === 'appointment';
                            const isDischarge = item.item_status_code === 'DISCHARGE';
                            const isCase = item.item_level === 'case';
                            const isFace2Face = item.item_level === 'referral' || item.item_level === 'referral1';
                            const isInvestigation = item.item_level === 'imaging';
                            const isPIFU = item.item_level === 'pifu';
                            const isOnboardedPIFU = item.appt_type_code === 'PIFUONB';
                            const isLabTestOrder = item.item_level === 'labs';

                            const isStatusDateAvailable = !!item.item_status_date;
                            const isStatusNewCase = item.item_status_code === 'NEW';

                            if (isLabTestOrder) {
                              return (
                                <LabTestOrderTimelineCard
                                  key={`${item.item_status_date}-${index}`}
                                  timelineItem={item}
                                />
                              );
                            }

                            return (
                              <Card key={`${item.item_status_date}-${index}`} className="itemBox ">
                                <div className="firstLine vl-flex-row-start-useableSize timeline-card">
                                  {isStatusDateAvailable ? (
                                    <FaRegCalendarAlt className="image" />
                                  ) : (
                                    <FaRegHospital className="image" />
                                  )}
                                  <span className="title">
                                    {isStatusDateAvailable ? (
                                      <Datestamp
                                        date={item.item_status_date}
                                        format="Long Date with day-of-week - Thursday, March 3rd, 2022"
                                      />
                                    ) : (
                                      item.site_name
                                    )}
                                  </span>
                                  {isAppointment && !isClinician && (
                                    <AppointmentActionMenu
                                      appointmentId={item.appt_id}
                                      refetchFn={refreshCasesData}
                                      extraActions={[
                                        {
                                          label: 'View Audit Trail',
                                          onClick: () => {
                                            setSelectedApptId(item.appt_id || item.item_id);
                                            setShowApptAuditModal(true);
                                          }
                                        }
                                      ]}
                                    />
                                  )}
                                  {isCase && !isClinician && (
                                    <ActionDropdown
                                      customDataTestID="timeline-tab-card-action-dropdown"
                                      actionItems={[
                                        {
                                          label: 'Update Case Ref',
                                          onClick: () => setShowChangeCaseRefModal(true)
                                        },
                                        {
                                          label: 'Discharge Case',
                                          onClick: () => setShowDischargeModal(true),
                                          isVisible: () =>
                                            !shower.some(
                                              (i) => i.item_status_code === 'DISCHARGE' && i.item_level === 'case'
                                            )
                                        },
                                        {
                                          label: 'Reopen Case',
                                          onClick: () => {
                                            reopenCaseMutation.mutate(pageState.case_id);
                                          },
                                          isVisible: () =>
                                            shower.some(
                                              (i) => i.item_status_code === 'DISCHARGE' && i.item_level === 'case'
                                            )
                                        },
                                        {
                                          label: 'Manage Requests',
                                          onClick: () => displayRequestManager(),
                                          isDisabled: () => {
                                            const investigation = timelineItems.find(
                                              (tl) => tl.item_level === 'imaging'
                                            );

                                            return (
                                              !investigation || item.item_status_code === 'DISCHARGE' || !isClientAdmin
                                            );
                                          }
                                        },
                                        ...(isStatusNewCase && !isDischarge && isClientAdmin
                                          ? [
                                              {
                                                label: 'Upload Documents',
                                                onClick: () => setUploadModalVisible(true)
                                              }
                                            ]
                                          : [])
                                      ]}
                                      rowData={{}}
                                    />
                                  )}
                                </div>
                                {isCase && ( //case ref
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Case Ref:')}</div>
                                    <div className="value">{item.client_case_id}</div>
                                  </div>
                                )}
                                {item.appt_date && item.item_level === 'dbreferral' && (
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Time:')}</div>
                                    <Datestamp date={item.appt_date} format="24 hour time - 09:47" />
                                  </div>
                                )}
                                {(isAppointment || isCase || isPIFU) && ( //imaging does not need time
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Time:')}</div>
                                    <div className="value">
                                      <Datestamp date={item.item_status_date} format="24 hour time - 09:47" />
                                    </div>
                                  </div>
                                )}
                                {isCase && ( //eligibility status
                                  <SuppressForRoleCode patient>
                                    <div className="text-row bottom-space">
                                      <div className="key">{t('Eligibility:')}</div>
                                      <div className="value">
                                        {item.case_status_desc}
                                        {item.case_status_date && (
                                          <>
                                            &nbsp;(
                                            <Datestamp
                                              date={item.case_status_date}
                                              format="Date only (localised) - 03/02/2022"
                                            />
                                            )
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </SuppressForRoleCode>
                                )}
                                {isCase && !isClinician && !!item.item_call_type_desc && (
                                  <div className="text-row bottom-space align-items-start">
                                    <div className="key">{t('Virtual Only Reason:')}</div>
                                    <div className="value">{item.item_call_type_desc}</div>
                                  </div>
                                )}
                                {isCase && !isClinician && !!item.item_type_desc && (
                                  <div className="text-row bottom-space align-items-start">
                                    <div className="key">{t('Cohort:')}</div>
                                    <div className="value">
                                      {item.item_type_desc}{' '}
                                      <CohortDateRange from={item.appt_date} to={item.ext_appt_date} />
                                    </div>
                                  </div>
                                )}
                                {!isCase && !isAppointment && !isPIFU && item.item_level !== 'dbreferral' && (
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Type:')}</div>
                                    <div className="value">
                                      {
                                        isFace2Face ? t('Face 2 Face') : `Diagnostics - ${item.item_type_desc}` // if was imaging
                                      }
                                    </div>
                                  </div>
                                )}
                                {(isAppointment || item.item_level === 'dbreferral') && (
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Type:')}</div>
                                    <div className="value">{item.item_type_desc}</div>
                                  </div>
                                )}
                                {(!(!isAppointment && !isDischarge) || isOnboardedPIFU) &&
                                  item.clinician_name &&
                                  item.clin_user_guid && (
                                    <div className="text-row bottom-space">
                                      <div className="key">{t('Call Type:')}</div>
                                      <div className="value">{item.item_call_type_desc}</div>
                                    </div>
                                  )}
                                {isAppointment && (
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Appointment ID:')}</div>
                                    <div className="value">{item.appt_id || item.item_id}</div>
                                  </div>
                                )}
                                {isFace2Face ? (
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Specialty:')}</div>
                                    <div className="value">{item.service_cat_desc}</div>
                                  </div>
                                ) : null}
                                {isFace2Face ? (
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Sub Specialty:')}</div>
                                    <div className="value">{item.service_type_desc}</div>
                                  </div>
                                ) : null}
                                {isFace2Face ? (
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Clinician referred to:')}</div>
                                    <div className="value">{item.clinician_name}</div>
                                  </div>
                                ) : null}
                                {item.site_name && isFace2Face && (
                                  <div className="text-row bottom-space">
                                    <div className="key" style={{ textWrap: 'nowrap' }}>
                                      {t('Hospital Site:')}
                                    </div>
                                    <div className="value">{item.site_name}</div>
                                  </div>
                                )}
                                {isPIFU && item.item_type_desc && (
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Outcome:')}</div>
                                    <div className="value">{item.item_type_desc}</div>
                                  </div>
                                )}
                                {isPIFU && item.item_status_date && (
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Expiry Date:')}</div>
                                    <Datestamp
                                      date={item.item_status_date}
                                      format="Long Date with day-of-week - Thursday, March 3rd, 2022"
                                    />
                                  </div>
                                )}
                                {isPIFU && item.item_call_type_desc && (
                                  <div
                                    className="bottom-space"
                                    style={{
                                      fontSize: '12px'
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontWeight: 'bold',
                                        marginRight: '2px'
                                      }}
                                    >
                                      Clinician&apos;s Advice:
                                    </div>
                                    <span dangerouslySetInnerHTML={{ __html: item.item_call_type_desc }} />
                                  </div>
                                )}
                                {item.clinician_name && item.item_level === 'dbreferral' && (
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Clinician:')}</div>
                                    <div className="value">{item.clinician_name}</div>
                                  </div>
                                )}
                                {item.clinician_name && item.title && item.item_level === 'dbreferral' && (
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Clinician Client:')}</div>
                                    <div className="value">{item.title}</div>
                                  </div>
                                )}
                                {item.site_name && item.item_level === 'dbreferral' && (
                                  <div className="text-row bottom-space">
                                    <div className="key" style={{ textWrap: 'nowrap' }}>
                                      {t('Hospital Site:')}
                                    </div>
                                    <div className="value">{item.site_name}</div>
                                  </div>
                                )}
                                {isInvestigation && (
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Site:')}</div>
                                    <div className="value">{item.site_name}</div>
                                  </div>
                                )}
                                {isInvestigation && (
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Date:')}</div>
                                    {item?.ext_appt_date ? (
                                      <Datestamp
                                        date={new Date(item.ext_appt_date)}
                                        format="Date @ Time (long, 24h) - Friday, March 3rd, 2022 @ 08:57"
                                      />
                                    ) : (
                                      <div>{t('N/A')}</div>
                                    )}
                                  </div>
                                )}
                                {isInvestigation && (
                                  <div className="text-row bottom-space flex-wrap overflow-row">
                                    <div className="key">{t('Updater:')}</div>
                                    <div className="value">{item.item_user}</div>
                                  </div>
                                )}
                                {isInvestigation && (
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Update Time:')}</div>
                                    <div className="value">
                                      <Datestamp date={new Date(item.item_status_date)} format="24 hour time - 09:47" />
                                    </div>
                                  </div>
                                )}

                                {!isStatusDateAvailable && !isDischarge ? (
                                  <div className="text-row bottom-space">
                                    <div className="key">{t('Date:')}</div>
                                    <div className="value">
                                      <Datestamp date={item.appt_date} format="24 hour time - 09:47" />
                                    </div>
                                  </div>
                                ) : null}

                                {!(!isAppointment && !isDischarge) && item.clinician_name && item.clin_user_guid && (
                                  <TimelineAvatar data={item} />
                                )}
                                {!(!isAppointment && !isDischarge) && item.clinician_name && item.title && (
                                  <div className="text-row top-space">
                                    <div className="key">{t('Clinician Client:')}</div>
                                    <div className="value">{item.title}</div>
                                  </div>
                                )}

                                <div className="footer">
                                  <div className="right-status">
                                    <Typography.Paragraph ellipsis={{ tooltip: true, rows: 8 }}>
                                      {item.item_status_desc || item.case_status_desc || item.appt_status_desc}
                                    </Typography.Paragraph>
                                  </div>
                                </div>
                              </Card>
                            );
                          })}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>
              </TabPane>
              <TabPane tabId="2">
                {canShowTasksTable && (
                  <DisplayForRoleCode hbsadmin clinician clinaudit audit super opshbs>
                    <TaskDisplay
                      client_case_id={selectedCase?.client_case_id}
                      case_id={pageState.case_id}
                      patient_guid={pageState.patient_guid}
                    />
                  </DisplayForRoleCode>
                )}
              </TabPane>

              <TabPane tabId="3">
                <DisplayForRoleCode hbsadmin audit clinician clinaudit super opshbs>
                  <section style={{ paddingTop: 10 }}>
                    <Container>
                      <h3 className="h6 mt-5 mb-3">{t('Appointment Call Recordings')}</h3>
                    </Container>
                    {enableDocumentsPreview && (
                      <RecordingsContainer
                        case_id={pageState.case_id ? parseInt(pageState.case_id) : undefined}
                        appointments={timelineItems?.filter((item) => item.item_level === 'appointment') || []}
                      />
                    )}
                  </section>

                  <section>
                    <Container>
                      <h3 className="h6 mt-5 mb-3">{t('Appointment Documents')}</h3>
                    </Container>

                    {documentData && (
                      <div key="1.5">
                        <TableDataView
                          noDataFoundText="No Documents Found"
                          headerItems={documentsHeaderItems}
                          dataSource={documentData}
                          onPaginationNumberClick={(pageNumberParam) => pageUpdatedDoc(pageNumberParam)}
                          paginationNumberOfScreens={countOfTimelinesCards}
                          keyValue="document_id"
                        />
                      </div>
                    )}

                    <div className="d-flex my-4 justify-content-center align-items-center">
                      <GeneratedPagination
                        onClick={(pageNumber) => pageUpdatedDoc(pageNumber)}
                        resetPagination={pageState.case_id}
                        dataLength={documentData?.length}
                        // maxDataLength is number of rows shown per page.
                        // Determined by BE - FE doesn't decide this. 5 for this endpoint
                        maxDataLength={5}
                      />
                    </div>
                  </section>

                  {caseHasNonCancelledAppointment && showClinicalNotes && (
                    <section>
                      <Container>
                        <h3 className="h6 my-3">{t('Clinical notes')}</h3>
                        <ClinicalNoteHistory apptId={pageState.appt_id} />
                      </Container>
                    </section>
                  )}

                  <section>
                    <Container>
                      <h3 className="h6 mt-5 mb-3">{t('Patient Data Access Logs')}</h3>
                    </Container>

                    {auditLog && (
                      <TableDataView
                        noDataFoundText="No Documents Found"
                        headerItems={[
                          { display: 'Accessed Name', dataSourceKey: 'actioned_user_name' },
                          {
                            display: 'Accessed Date',
                            dataSourceKey: 'actioned_date',
                            columnDataFormat: ({ data: date }) => (
                              <Datestamp
                                date={date}
                                format="Date @ Time (long, 24h) - Friday, March 3rd, 2022 @ 08:57"
                              />
                            )
                          },
                          { display: 'Reason', dataSourceKey: 'reason' }
                        ]}
                        dataSource={auditLog}
                        onPaginationNumberClick={(pageNumberParam) => pageUpdatedLog(pageNumberParam)}
                        paginationNumberOfScreens={countOfTimelinesCards}
                      />
                    )}
                    <div className="d-flex my-4 justify-content-center align-items-center">
                      <GeneratedPagination
                        onClick={(pageNumber) => pageUpdatedLog(pageNumber)}
                        resetPagination={pageState.case_id}
                        dataLength={auditLog?.length}
                        // maxDataLength is number of rows shown per page.
                        // Determined by BE - FE doesn't decide this. 10 for this endpoint
                        maxDataLength={10}
                      />
                    </div>
                  </section>
                </DisplayForRoleCode>
              </TabPane>
              {featureCaseLevelNotes && (
                <TabPane tabId="4">
                  <PatientNotesPanel
                    patient_guid={pageState.patient_guid}
                    case_id={pageState.case_id}
                    onMessagesLoaded={setCaseNoteCount}
                  />
                </TabPane>
              )}
            </TabContent>
            <br />
          </div>

          <div className="patient-notes">
            <PatientNotesPanel
              title={
                <Badge count={numPatientNotes} offset={[10, -10]}>
                  <h5>{t('Patient Notes')}</h5>
                </Badge>
              }
              patient_guid={pageState.patient_guid}
              case_id={pageState.case_id}
              onMessagesLoaded={setNumPatientNotes}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReferrerViewTimeLine;
